/**
 * deviceDetect.ts — Elite Nexus AI Device / OS / Browser Detection Utility
 *
 * Detects the user's device type, operating system, and browser at runtime.
 * Used to conditionally render mobile-optimised components (e.g. NeuralDeploymentGrid)
 * and apply platform-specific rendering fixes.
 *
 * All detection is purely client-side via navigator.userAgent + feature sniffing.
 * No external libraries required.
 */

export type DeviceType = 'mobile' | 'tablet' | 'desktop';
export type OSType = 'ios' | 'android' | 'windows' | 'macos' | 'linux' | 'unknown';
export type BrowserType = 'chrome' | 'safari' | 'firefox' | 'edge' | 'samsung' | 'opera' | 'unknown';

export interface DeviceInfo {
  deviceType: DeviceType;
  os: OSType;
  browser: BrowserType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isSamsung: boolean;
  isSafari: boolean;
  isChrome: boolean;
  isFirefox: boolean;
  isEdge: boolean;
  /** True when canvas/RAF performance may be degraded (older Android WebView) */
  isLowPerfDevice: boolean;
  /** True when the device supports touch events */
  hasTouch: boolean;
  /** True when prefers-reduced-motion is active */
  prefersReducedMotion: boolean;
}

function detectOS(ua: string): OSType {
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  if (/windows/i.test(ua)) return 'windows';
  if (/macintosh|mac os x/i.test(ua)) return 'macos';
  if (/linux/i.test(ua)) return 'linux';
  return 'unknown';
}

function detectBrowser(ua: string): BrowserType {
  // Order matters — Samsung Internet contains "Chrome", Edge contains "Chrome"
  if (/samsungbrowser/i.test(ua)) return 'samsung';
  if (/edg\//i.test(ua)) return 'edge';
  if (/opr\//i.test(ua) || /opera/i.test(ua)) return 'opera';
  if (/firefox/i.test(ua)) return 'firefox';
  // Safari must be checked before Chrome (Chrome UA contains "Safari")
  if (/safari/i.test(ua) && !/chrome/i.test(ua)) return 'safari';
  if (/chrome/i.test(ua)) return 'chrome';
  return 'unknown';
}

function detectDeviceType(ua: string): DeviceType {
  // iPad on iOS 13+ reports as Macintosh — check touch points
  const isIPadOS = /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  if (/ipad/i.test(ua) || isIPadOS) return 'tablet';
  if (/android/i.test(ua) && !/mobile/i.test(ua)) return 'tablet'; // Android tablet
  if (/iphone|ipod|android.*mobile|windows phone|blackberry|bb\d+|meego|silk/i.test(ua)) return 'mobile';
  return 'desktop';
}

/**
 * Returns a full DeviceInfo snapshot. Safe to call during render — reads
 * navigator.userAgent synchronously. Returns a safe desktop fallback in SSR.
 */
export function getDeviceInfo(): DeviceInfo {
  // SSR / non-browser guard
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      deviceType: 'desktop', os: 'unknown', browser: 'unknown',
      isMobile: false, isTablet: false, isDesktop: true,
      isIOS: false, isAndroid: false, isSamsung: false,
      isSafari: false, isChrome: false, isFirefox: false, isEdge: false,
      isLowPerfDevice: false, hasTouch: false, prefersReducedMotion: false,
    };
  }

  const ua = navigator.userAgent;
  const os = detectOS(ua);
  const browser = detectBrowser(ua);
  const deviceType = detectDeviceType(ua);

  // Low-perf: Android < 9 or WebView
  const androidVersion = ua.match(/android (\d+)/i);
  const isLowPerfDevice =
    (os === 'android' && androidVersion ? parseInt(androidVersion[1]) < 9 : false) ||
    /wv\)/.test(ua); // WebView flag

  const hasTouch =
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0;

  const prefersReducedMotion =
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  return {
    deviceType,
    os,
    browser,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',
    isIOS: os === 'ios',
    isAndroid: os === 'android',
    isSamsung: browser === 'samsung',
    isSafari: browser === 'safari',
    isChrome: browser === 'chrome',
    isFirefox: browser === 'firefox',
    isEdge: browser === 'edge',
    isLowPerfDevice,
    hasTouch,
    prefersReducedMotion,
  };
}

/**
 * React hook — memoises the DeviceInfo object for the component lifetime.
 * Re-evaluates on mount only (UA never changes mid-session).
 */
import { useMemo } from 'react';
export function useDeviceInfo(): DeviceInfo {
  return useMemo(() => getDeviceInfo(), []);
}
